import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  SquaresFour, Database, FileText, GearSix, Planet, Waveform, Flask, StackSimple,
  Cube, Crosshair, Sparkle, ShieldCheck, ArrowLeft, MapTrifold,
} from "@phosphor-icons/react";

// Project-scoped, collapsible (hover-to-expand) module navigation for the SYMIN analytical shell. Light theme only.
export function ModuleNav({ pid, active }) {
  const nav = useNavigate();
  const loc = useLocation();

  const built = [
    { id: "command", label: "Project Command", icon: SquaresFour, to: `/projects/${pid}/command`, accent: "#9E040E" },
    { id: "map", label: "GIS Map Suite", icon: MapTrifold, to: `/projects/${pid}/gis`, accent: "#2563EB" },
    { id: "rs", label: "Remote Sensing / EO", icon: Planet, to: `/projects/${pid}/eo`, accent: "#7C3AED" },
    { id: "geology", label: "Geology & Structure", icon: StackSimple, to: `/projects/${pid}/geology`, accent: "#059669" },
    { id: "geochem", label: "Geochemistry", icon: Flask, to: `/projects/${pid}/geochem`, accent: "#D9480F" },
    { id: "geophys", label: "Geophysics", icon: Waveform, to: `/projects/${pid}/geophysics`, accent: "#0891B2" },
    { id: "ai", label: "AI Geoscientist", icon: Sparkle, to: `/projects/${pid}/ai`, accent: "#7C3AED" },
    { id: "targets", label: "Target Generation", icon: Crosshair, to: `/projects/${pid}/targets`, accent: "#9E040E" },
    { id: "drilling", label: "Drilling & 3D", icon: Cube, to: `/projects/${pid}/3d`, accent: "#2563EB" },
    { id: "fusion", label: "Multi-Sensor Fusion", icon: StackSimple, to: `/projects/${pid}/fusion`, accent: "#D97706" },
    { id: "qaqc", label: "QA/QC & Alerts", icon: ShieldCheck, to: `/projects/${pid}/qaqc`, accent: "#E11D48" },
    { id: "data", label: "Data & Setup", icon: Database, to: `/projects/${pid}`, accent: "#2563EB" },
    { id: "reports", label: "Technical Reports", icon: FileText, to: `/projects/${pid}`, accent: "#059669" },
    { id: "settings", label: "Project Settings", icon: GearSix, to: `/projects/${pid}/advanced`, accent: "#64748B" },
  ];
  const soon = [];

  const Item = ({ item, disabled }) => {
    const Icon = item.icon;
    const isActive = !disabled && item.id === active;
    return (
      <button
        key={item.id}
        data-testid={`module-nav-${item.id}`}
        onClick={() => (disabled ? toast.info(`${item.label} - arriving in an upcoming SYMIN release`) : nav(item.to))}
        className={`group/item relative flex items-center gap-3 h-10 px-3 rounded-md text-sm transition-colors w-full ${
          isActive ? "bg-red-50 text-slate-900" : disabled ? "text-slate-400 hover:bg-slate-50" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`}
      >
        {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full" style={{ background: "#C20511" }} />}
        <Icon size={20} weight={isActive ? "fill" : "regular"} className="shrink-0" style={{ color: isActive ? "#C20511" : undefined }} />
        <span className="opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap flex-1 text-left">{item.label}</span>
        {disabled && <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[9px] font-mono uppercase tracking-wider text-slate-400 border border-slate-300 rounded px-1">Soon</span>}
      </button>
    );
  };

  return (
    <aside className="group w-[64px] hover:w-[240px] transition-[width] duration-200 border-r border-slate-200 bg-white flex flex-col fixed h-screen z-[1000] overflow-hidden">
      <div className="h-[52px] flex items-center gap-3 px-3.5 border-b border-slate-200 shrink-0">
        <div className="w-9 h-9 rounded-md bg-slate-50 grid place-items-center shrink-0 p-1 border border-slate-200">
          <img src="/symin-mark.png" alt="SYMIN" className="h-full w-full object-contain" />
        </div>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity font-display font-bold text-slate-900 tracking-tight">SYMIN</span>
      </div>

      <div className="flex-1 overflow-y-auto py-3 px-2 flex flex-col gap-0.5">
        <button data-testid="module-nav-projects" onClick={() => nav("/projects")}
          className="flex items-center gap-3 h-9 px-3 rounded-md text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors mb-1">
          <ArrowLeft size={18} className="shrink-0" />
          <span className="opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">All Projects</span>
        </button>
        {built.map((m) => <Item key={m.id} item={m} />)}
        {soon.length > 0 && (
          <>
            <div className="my-2 mx-3 border-t border-slate-200" />
            <span className="opacity-0 group-hover:opacity-100 transition-opacity px-3 text-[9px] font-mono uppercase tracking-[0.18em] text-slate-400 mb-1">Roadmap Modules</span>
            {soon.map((m) => <Item key={m.id} item={m} disabled />)}
          </>
        )}
      </div>

      <div className="p-3 border-t border-slate-200 shrink-0">
        <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">SYMIN · Geo Explorer</span>
      </div>
    </aside>
  );
}
